import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import {
  applyDocumentPreferences,
  type CalendarPreferences,
  readStoredPreferences,
  writeStoredPreferences,
} from "./preferences.ts";

interface PreferencesContextValue {
  prefs: CalendarPreferences;
  setPrefs: (patch: Partial<CalendarPreferences>) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefsState] = useState<CalendarPreferences>(() => {
    const initial = readStoredPreferences();
    if (typeof document !== "undefined") applyDocumentPreferences(initial);
    return initial;
  });

  const setPrefs = useCallback((patch: Partial<CalendarPreferences>) => {
    setPrefsState((current) => {
      const next = { ...current, ...patch };
      writeStoredPreferences(next);
      applyDocumentPreferences(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ prefs, setPrefs }), [prefs, setPrefs]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const value = useContext(PreferencesContext);
  if (value === null) {
    throw new Error("usePreferences precisa estar dentro de PreferencesProvider");
  }
  return value;
}
